import * as L from 'leaflet'
import type { TileLayerOptions } from 'leaflet'

interface TileFunction {
  (view: any): string | Promise<string>
}

class TileLayerFunctional extends L.TileLayer {
  private _tileFunction: TileFunction

  constructor(tileFunction: TileFunction, options?: TileLayerOptions) {
    super('', options)
    this._tileFunction = tileFunction
  }

  override getTileUrl(tilePoint: L.Point): any {
    const map = this._map
    const crs = map.options.crs as L.CRS
    const tileSize = this.options.tileSize as number
    const zoom = (tilePoint as any).z
    const nwPoint = tilePoint.multiplyBy(tileSize)
    const sePoint = nwPoint.add(new L.Point(tileSize, tileSize))
    const nw = crs.project(map.unproject(nwPoint, zoom))
    const se = crs.project(map.unproject(sePoint, zoom))
    const bbox = [nw.x, se.y, se.x, nw.y].join(',')

    const view = {
      bbox,
      width: tileSize,
      height: tileSize,
      zoom,
      tile: {
        row: this.options.tms
          ? (this as any)._tileNumBounds.max.y - tilePoint.y
          : tilePoint.y,
        column: tilePoint.x,
      },
      subdomain: (this as any)._getSubdomain(tilePoint),
    }

    return this._tileFunction(view)
  }

  _loadTile(tile: any, tilePoint: L.Point): void {
    tile._layer = this
    tile.onload = this._tileOnLoad
    tile.onerror = this._tileOnError
    ;(this as any)._adjustTilePoint(tilePoint)
    const tileUrl = this.getTileUrl(tilePoint) as any

    if (typeof tileUrl === 'string') {
      ;(this as any).src = tileUrl
      this.fire('tileloadstart', { tile, url: tile.src })
    } else if (tileUrl && typeof tileUrl.then === 'function') {
      tileUrl.then((tileUrl: any) => {
        tile.src = tileUrl
        this.fire('tileloadstart', { tile, url: tile.src })
      })
    }
  }
}

function tileLayerFunctional(
  tileFunction: TileFunction,
  options?: TileLayerOptions
): TileLayerFunctional {
  return new TileLayerFunctional(tileFunction, options)
}

export { TileLayerFunctional, tileLayerFunctional }
